/**
 * Client mirror of the server's `CollectionProvider` entity.
 *
 * Source of truth: `SnowfallPressServerless/src/entity/collectionprovider.ts`.
 * Shared contract: `docs/contracts/shared/CollectionProvider.md`.
 * Inbox note: `docs/contracts/inbox/_processed/2026-07-08-collectionprovider-endpoints-and-ui-wiring.md`.
 *
 * A CollectionProvider is the payment-routing owner for one or more
 * ServiceProviders. Resolution chain used by the server's payment
 * layer:
 *
 *   Organization.serviceProvider_ID
 *     → ServiceProvider.collectionprovider_ID
 *     → CollectionProvider.paymentSettings (JSON string)
 *
 * `paymentSettings` is a JSON-string blob (parallel to
 * `ServiceProvider.rateConfig` / `.tax` / etc.). Expected keys include
 * the CardPointe credentials the server needs to reach the live
 * processor (see `getPaymentSettings()` doc for the full list). The
 * server treats environment variables of the same names as
 * fallback-only when a matching key isn't present in the JSON.
 */
export class CollectionProvider {
  ID: number;
  Name: string = '';
  admin_ID: number = 0;
  /**
   * JSON-string blob of payment-routing settings and CardPointe
   * credentials. See {@link getPaymentSettings} / {@link setPaymentSettings}
   * for parse / serialize helpers that tolerate the server returning
   * this column as either a JSON string or (in some code paths) an
   * already-parsed object.
   *
   * Known keys — matches the server's payment-resolver (canonical shape
   * is documented in `docs/contracts/shared/PaymentProcessors.md`):
   *  - PAYMENT_PROCESSOR                                (enum, see PaymentProcessors.md §2)
   *  - PAYMENT_PROCESSOR_SP_<serviceProviderId>
   *  - PAYMENT_MERCHANT_ACCOUNT_SP_<serviceProviderId>
   *  - CARDPOINTE_GATEWAY_BASE_URL
   *  - CARDPOINTE_CARDSECURE_BASE_URL   (optional — falls back to gateway base)
   *  - CARDPOINTE_API_USERNAME
   *  - CARDPOINTE_API_PASSWORD
   *  - CARDPOINTE_HTTP_TIMEOUT_MS       (optional)
   */
  paymentSettings: string | null = null;
  lastUpdate: Date;

  constructor(obj?: any) {
    if (obj) {
      for (const prop of Object.keys(obj)) {
        this[prop] = obj[prop];
      }
    }
  }

  /**
   * Parse {@link paymentSettings} into a plain key/value object. Returns
   * `{}` on missing / invalid JSON so the editor can populate a fresh
   * blob without special-casing null / undefined.
   *
   * Tolerates the server handing the column back either as a raw JSON
   * string (the storage shape) or as an already-parsed object (some
   * controllers pre-parse JSON-string columns on GET). Mirrors the
   * pattern used by `ServiceProvider.getRateConfig()`.
   */
  getPaymentSettings(): { [key: string]: any } {
    const v: any = this.paymentSettings;
    if (v && typeof v === 'object') { return v as { [key: string]: any }; }
    if (typeof v === 'string' && v.length > 0) {
      try { return JSON.parse(v) as { [key: string]: any }; }
      catch (e) {
        console.log(`Error parsing CollectionProvider.paymentSettings for CP ${this.ID}`);
      }
    }
    return {};
  }

  /** Serialize a key/value object into {@link paymentSettings}. */
  setPaymentSettings(settings: { [key: string]: any }): void {
    this.paymentSettings = JSON.stringify(settings || {});
  }
}

